import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import mandate from "@/assets/images/mandate.svg";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import PrimaryButtonB2B from "@/b2b/components/PrimaryButton";
import Div100vh from "react-div-100vh";
import { clickMandateAction } from "../helper";
import { fetchJourneyWithGates } from "@/b2b/lib/journeyGates";
import Loader from "@/components/common/Loader";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import HousrMandate from "@/assets/images/housr-mandate.svg";
import Header from "@/b2b/components/Header";

export default function Mandate({ theme }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  // mode "new-tab": the bank page cannot send the tenant back, so poll the step from here.
  const [bankUrl, setBankUrl] = useState("");
  // Gromor's bank page opens in a new tab, and preparing it takes Gromor ~20 s (`POST /NACH`),
  // so only that journey gets the "this can take a while" note. Every other lender keeps its
  // same-tab flow untouched.
  const [opensInNewTab, setOpensInNewTab] = useState(false);
  useEffect(() => {
    let active = true;
    fetchJourneyWithGates()
      .then(({ journey } = {}) => {
        if (active) setOpensInNewTab(journey?.lender === "GROMOR");
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  let mandateImg;

  if (theme?.name === "housr") mandateImg = HousrMandate;
  else mandateImg = mandate;

  useEffect(() => {
    if (!bankUrl) return undefined;
    const timer = setInterval(async () => {
      const { journey } = await fetchJourneyWithGates().catch(() => ({}));
      const status = journey?.steps?.find((s) => s.name === "EMANDATE")?.status;
      if (status === "SUCCESS") {
        navigate(`/b/${localStorage.getItem("user_identifier")}?partner=${localStorage.getItem("partnerIdentifier")}&step=onboarding`);
      } else if (status === "FAILED" || status === "IN_REVIEW") {
        setBankUrl("");
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [bankUrl]);

  // Tap, wait here, then tap a real link. We used to open a blank tab inside the tap and fill it
  // when the bank URL arrived — on production (28 Sep) the tenant stared at a white tab for ~20 s
  // while Gromor prepared the mandate, and thought it had broken. Now the page shows the wait,
  // and the tab opens straight onto the bank page from a link the tenant taps (never blocked).
  const handleClick = async () => {
    setIsLoading(true); // Disable buttons
    try {
      const data = await clickMandateAction();
      if (data?.mode === "new-tab") setBankUrl(data.sessionUrl);
    } catch (error) {
      toast({
        title: "Something went wrong",
        description: error.message,
        action: (
          <ToastAction
            onClick={() => {
              window.location.href = "/home";
            }}
            className="outline-none border-none bg-primary"
            altText="Try again"
          >
            Back Home
          </ToastAction>
        ),
      });
    } finally {
      setIsLoading(false);
    } // Wait for the action to complete
  };

  return (
    <Div100vh
      className={`flex flex-col justify-between py-10  overflow-y-scroll ${
        theme?.backgroundColor && theme.backgroundColor
      }`}
    >
      <div className="px-6">
        <Header />
      </div>
      <img src={mandateImg} alt="mandate" />

      <div className="mx-6 mt-4">
        {bankUrl ? (
          <div className="flex flex-col items-center gap-3">
            <p className="text-center text-sm text-gray-700">
              Your bank page is ready. It opens in a new tab: finish setting up the auto-debit
              there, then come back here.
            </p>
            {/* A real link, so the tap itself opens the tab — never blocked, never blank. */}
            <a
              href={bankUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-bank-page"
              className={`w-full rounded-lg py-3 text-center font-medium text-white ${
                theme?.primaryButtonBgColor || "bg-primary"
              }`}
            >
              Open bank page
            </a>
          </div>
        ) : (
          <>
            {theme?.headingColor ? (
              <PrimaryButtonB2B
                text="Continue"
                handleClick={handleClick}
                loading={isLoading}
              />
            ) : (
              <PrimaryButton
                text="Continue"
                handleClick={handleClick}
                loading={isLoading}
              />
            )}
            {isLoading && opensInNewTab && (
              <p
                className="mt-3 text-center text-xs text-gray-600"
                data-testid="preparing-bank-page"
              >
                Preparing your bank page — this can take up to 30 seconds. Please stay on this
                page.
              </p>
            )}
          </>
        )}
      </div>
    </Div100vh>
  );
}
